import { useMemo } from 'react';

import {
  IInspectionErknmOrganization,
  ItAccreditationResponseOrgStatusDictionary,
} from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG } from 'CorePackage/MainModule/constants/dateFormat';

import styles from './styles.module.scss';
import { KnmSubjectTypesEnum } from '../enums/KnmSubjectTypesEnum';
import { DecisionSubjectTypesEnum } from '../enums/DecisionSubjectTypesEnum';

interface IErknmFaceProps {
  organization: IInspectionErknmOrganization;
  index: number;
}

export const ErknmFaceOrganization = ({ organization, index }: IErknmFaceProps) => {
  const {
    isSonko,
    country,
    snils,
    document,
    locationAddress,
    workAddress,
    rafpNza,
    subjectType,
    factualAddress,
    mspCode,
  } = organization;

  const isUR = subjectType === DecisionSubjectTypesEnum.UR;
  const isIUR = subjectType === DecisionSubjectTypesEnum.IUR;
  const isIP = subjectType === DecisionSubjectTypesEnum.IP;
  const isFIUR = subjectType === DecisionSubjectTypesEnum.FIUR;
  const isIFIZ = subjectType === DecisionSubjectTypesEnum.IFIZ;

  const isNotMsp = !mspCode || mspCode === 'NON_EXIST';

  const organizationNameColText = useMemo(() => {
    switch (subjectType) {
      case KnmSubjectTypesEnum.FIZ:
      case KnmSubjectTypesEnum.IFIZ:
        return 'Фамилия, имя, отчество (при наличии)';
      case KnmSubjectTypesEnum.IUR:
        return 'Наименование';
      case KnmSubjectTypesEnum.FIUR:
        return 'Наименование организации';
      default:
        return 'Наименование проверяемого лица';
    }
  }, [subjectType]);

  return (
    <div style={index !== 0 ? { marginTop: '20px' } : {}}>
      <span className={styles.SectionTitle}>Контролируемое лицо №{index + 1}</span>
      {subjectType && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Тип контролируемого лица</div>
          <div className={styles.ColValue}>{subjectType}</div>
        </div>
      )}
      {organization.inn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Идентификационный номер налогоплательщика (ИНН)</div>
          <div className={styles.ColValue}>{organization.inn}</div>
        </div>
      )}
      {organization.ogrn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Основной государственный регистрационный номер (ОГРН)</div>
          <div className={styles.ColValue}>{organization.ogrn}</div>
        </div>
      )}
      {snils && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Страховой номер индивидуального лицевого счета (СНИЛС)</div>
          <div className={styles.ColValue}>{snils}</div>
        </div>
      )}
      {rafpNza && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Номер записи об аккредитации (НЗА)</div>
          <div className={styles.ColValue}>{rafpNza}</div>
        </div>
      )}
      {organization.organizationName && (
        <div className={styles.Row}>
          <div className={styles.ColText}>{organizationNameColText}</div>
          <div className={styles.ColValue}>{organization.organizationName}</div>
        </div>
      )}
      {country && isIUR && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Государство, в котором указанное юридическое лицо зарегистрировано</div>
          <div className={styles.ColValue}>{country}</div>
        </div>
      )}
      {country && isIFIZ && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Гражданство (при наличии)</div>
          <div className={styles.ColValue}>{country}</div>
        </div>
      )}
      {(isUR || isIP || isFIUR) && (
        <div className={styles.Row}>
          <div className={styles.ColText}>
            Сведения об отнесении контролируемого лица к субъектам малого и среднего бизнеса
          </div>
          <div className={styles.ColValue}>{isNotMsp ? 'Нет' : 'Да'}</div>
        </div>
      )}
      {organization.okveds && (
        <>
          <span className={styles.SectionTitle}>ОКВЭД</span>
          {organization.okveds.map((itemOkveds, indexOkveds) => (
            <div key={index}>
              <span className={styles.SectionTitle}>ОКВЭД №{indexOkveds + 1}</span>
              {itemOkveds.name && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{itemOkveds.name}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {document && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Сведения о документе, удостоверяющем личность</div>
          <div className={styles.ColValue}>{document}</div>
        </div>
      )}
      {locationAddress && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Адрес места нахождения</div>
          <div className={styles.ColValue}>{locationAddress}</div>
        </div>
      )}
      {workAddress && !isIP && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Место нахождения (осуществления деятельности) контролируемого лица</div>
          <div className={styles.ColValue}>{workAddress}</div>
        </div>
      )}
      {factualAddress && !isIP && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Место фактического осуществления деятельности</div>
          <div className={styles.ColValue}>{factualAddress}</div>
        </div>
      )}
      {typeof isSonko === 'boolean' && (isUR || isFIUR) && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие проверяемого лица в реестре СОНКО</div>
          <div className={styles.ColValue}>{isSonko ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {organization.isPresentInItRegistry !== undefined && isUR && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие в реестре аккредитованных IT-организаций</div>
          <div className={styles.ColValue}>{organization.isPresentInItRegistry ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {organization.isPresentInItRegistry && organization.itAccreditationResponse && (
        <>
          <div className={styles.Row}>
            <div className={styles.ColText}>Регистрационный номер</div>
            <div className={styles.ColValue}>{organization.itAccreditationResponse.registryNumber}</div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата включения</div>
            <div className={styles.ColValue}>
              {formatDate({
                date: organization.itAccreditationResponse.dateDecisionAccreditation || '',
                dateFormat: DATE_FORMAT_LONG,
              })}
            </div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Статус</div>
            <div className={styles.ColValue}>
              {
                ItAccreditationResponseOrgStatusDictionary[
                  organization.itAccreditationResponse.organizationStatus || 'UNKNOWN'
                ]
              }
            </div>
          </div>
        </>
      )}
    </div>
  );
};
